import React from 'react';
import { useApp } from '@/hooks/use-app-context';
import { translations, languages, LanguageCode } from '@/translations/translations';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';

interface LanguageSelectorProps {
  variant?: 'default' | 'compact' | 'minimal' | 'header' | 'footer' | 'admin';
  className?: string;
}

export function LanguageSelector({ variant = 'default', className = '' }: LanguageSelectorProps) {
  const { language, setLanguage } = useApp();
  const { toast } = useToast();
  const t = translations[language];

  const handleLanguageChange = (newLanguage: LanguageCode) => {
    if (newLanguage === language) return;

    setLanguage(newLanguage);

    toast({
      title: translations[newLanguage].languageChanged,
      duration: 2000,
    });
  };

  const currentLanguage = languages[language];

  if (!currentLanguage) {
    console.error('Language not found:', language, 'Available languages:', languages);
    return <div>Language error</div>;
  }

  if (variant === 'header') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t.selectLanguage || 'Select language'}
            className={cn(
              'group/lang relative inline-flex h-10 items-center gap-1.5 rounded-full border border-transparent bg-transparent px-3 text-sm font-medium text-muted-foreground transition-all duration-300 ease-premium hover:border-border/45 hover:bg-background/45 hover:text-foreground focus-ring data-[state=open]:border-primary/20 data-[state=open]:bg-primary/[0.07] data-[state=open]:text-primary',
              className
            )}
          >

            <span className="uppercase">
              {language}
            </span>
            <Icon name="chevronDown" className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-data-[state=open]/lang:rotate-180" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={12}
          className="w-[190px] overflow-hidden rounded-2xl border border-border/55 bg-background/96 p-0 text-foreground shadow-[0_26px_76px_-42px_hsl(222_47%_4%/0.58)] backdrop-blur-2xl supports-[backdrop-filter]:bg-background/88"
        >
          <div className="relative border-b border-border/40 px-4 py-3">
            <div className="flex items-center gap-2 text-[0.62rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              <span>{t.selectLanguage || 'Language'}</span>
            </div>
          </div>

          <div className="p-2">
            {Object.entries(languages).map(([code, lang]) => {
              const isActive = code === language;

              return (
                <DropdownMenuItem
                  key={code}
                  onClick={() => handleLanguageChange(code as LanguageCode)}
                  className={cn(
                    'group/item relative flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2.5 outline-none transition-all duration-200 focus:bg-muted/[0.16] focus:text-foreground',
                    isActive ? 'bg-primary/[0.08] text-primary' : 'text-foreground hover:bg-muted/[0.16]'
                  )}
                >
                  <span
                    className={cn(
                      'absolute inset-y-2 left-0 w-px rounded-full bg-primary transition-opacity duration-200',
                      isActive ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="min-w-0 flex-1 pl-2">
                    <span className="block truncate text-sm font-medium tracking-[-0.015em]">
                      {lang.name}
                    </span>
                  </span>
                  <Icon
                    name="check"
                    className={cn('h-3.5 w-3.5 transition-opacity duration-200', isActive ? 'opacity-100' : 'opacity-0')}
                  />
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'minimal') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${className}`}
          >
            <span className="text-lg">{currentLanguage.flag}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {Object.entries(languages).map(([code, lang]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageChange(code as LanguageCode)}
              className="flex cursor-pointer items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
              </div>
              {code === language && <Icon name="check" className="h-3 w-3" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`flex h-9 items-center gap-2 ${className}`}
          >
            <span>{currentLanguage.flag}</span>
            <span className="hidden text-sm sm:inline">{currentLanguage.code.toUpperCase()}</span>
            <Icon name="chevronDown" className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {Object.entries(languages).map(([code, lang]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageChange(code as LanguageCode)}
              className="flex cursor-pointer items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
              </div>
              {code === language && <Icon name="check" className="h-4 w-4 text-yodi-orange" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'admin') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t.selectLanguage || 'Select language'}
            className={cn(
              'group/lang inline-flex h-10 items-center gap-2 rounded-full border border-border/40 bg-slate-900/[0.025] dark:bg-white/[0.025] px-3 text-sm font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:border-border/40 hover:bg-slate-900/[0.045] dark:hover:bg-white/[0.045] hover:text-foreground data-[state=open]:border-brand/20 data-[state=open]:bg-brand/[0.06] data-[state=open]:text-brand dark:text-brand',
              className
            )}
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-black/20 text-[0.68rem]">{currentLanguage.flag}</span>
            <span className="text-xs font-bold uppercase tracking-[0.12em]">{language}</span>
            <Icon name="chevronDown" className="h-3.5 w-3.5 text-slate-500 transition-transform duration-300 group-data-[state=open]/lang:rotate-180 group-data-[state=open]/lang:text-brand dark:text-brand" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={12}
          className="w-[230px] overflow-hidden rounded-2xl border border-slate-900/10 dark:border-white/[0.08] bg-popover dark:bg-[#080d19]/98 p-0 text-slate-900 dark:text-slate-100 shadow-[0_26px_76px_rgba(0,0,0,0.42)] backdrop-blur-2xl"
        >
          <div className="relative border-b border-slate-900/10 dark:border-white/[0.065] px-4 py-3">
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-brand/35 to-transparent" />
            <div className="flex items-center gap-2 text-[0.62rem] font-semibold uppercase tracking-[0.24em] text-slate-500">
              <span>{t.selectLanguage || 'Language'}</span>
            </div>
          </div>

          <div className="p-2">
            {Object.entries(languages).map(([code, lang]) => {
              const isActive = code === language;

              return (
                <DropdownMenuItem
                  key={code}
                  onClick={() => handleLanguageChange(code as LanguageCode)}
                  className={cn(
                    'group/item relative flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 outline-none transition-colors focus:bg-slate-900/[0.055] dark:focus:bg-white/[0.055] focus:text-slate-900 dark:focus:text-white',
                    isActive ? 'bg-brand/[0.07] text-brand' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-900/[0.04] dark:hover:bg-white/[0.04]'
                  )}
                >
                  <span className={cn('absolute inset-y-3 left-0 w-px rounded-full bg-brand transition-opacity', isActive ? 'opacity-100' : 'opacity-0')} />
                  <span className="grid h-8 w-8 place-items-center rounded-full border border-slate-900/10 dark:border-white/[0.075] bg-slate-900/[0.025] dark:bg-white/[0.025] text-sm">{lang.flag}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold tracking-[-0.015em]">{lang.name}</span>
                    <span className="mt-0.5 block text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-slate-500">{code}</span>
                  </span>
                  <Icon name="check" className={cn('h-3.5 w-3.5 text-brand transition-opacity', isActive ? 'opacity-100' : 'opacity-0')} />
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (variant === 'footer') {
    return (
      <div className={`inline-flex items-center rounded-full border border-border/60 bg-background/70 p-1 backdrop-blur-sm ${className}`}>
        {Object.entries(languages).map(([code, lang]) => {
          const isActive = code === language;

          return (
            <button
              key={code}
              type="button"
              onClick={() => handleLanguageChange(code as LanguageCode)}
              className={`ease-premium inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-all ${isActive ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <span>{lang.flag}</span>
              <span className="font-medium uppercase tracking-wide">{code}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="rounded-lg border border-border/50 bg-background/30 p-2">
        <Icon name="globe" className="h-4 w-4 text-muted-foreground" />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="flex min-w-[120px] items-center gap-2 border-border/50 bg-background/30 transition-all hover:bg-background/50"
          >
            <span>{currentLanguage.flag}</span>
            <span className="text-sm">{currentLanguage.name}</span>
            <Icon name="chevronDown" className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <div className="border-b border-border/50 px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {t.selectLanguage}
          </div>
          {Object.entries(languages).map(([code, lang]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageChange(code as LanguageCode)}
              className="flex cursor-pointer items-center justify-between py-2"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{lang.flag}</span>
                <div>
                  <div className="font-medium">{lang.name}</div>
                  <div className="text-xs text-muted-foreground">{code.toUpperCase()}</div>
                </div>
              </div>
              {code === language && (
                <div className="flex items-center gap-1">
                  <Icon name="check" className="h-4 w-4 text-yodi-orange" />
                </div>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}